<script>
import AutocompleteMany from '@components/autocomplete-many/autocomplete-many.vue'
import TagsField from '@components/tags-field'
import rules from '@constants/Rules'
import {
  BENCHMARK_INDEXADORES,
  buildClienteBenchmarkFormula,
  parseClienteBenchmarkFormula,
  normalizeClienteBenchmarkIndexador,
} from '@utils/clienteBenchmark'
import ExpandableGroupTable from '@modules/crm/components/expandable-group-table/expandable-group-table.vue'
import {
  CLIENT_STATUS_TOGGLE_OPTIONS,
  CLIENT_ACCESS_TOGGLE_OPTIONS,
} from '@modules/clients/constants/Text'

export default {
  name: 'ClientFields',
  components: {
    AutocompleteMany,
    TagsField,
    ExpandableGroupTable,
  },
  props: {
    mode: {
      type: String,
      default: 'create',
      validator: (value) => ['create', 'edit'].includes(value),
    },
    profileItems: {
      type: Array,
      default: () => [],
    },
    profileLoading: {
      type: Boolean,
      default: false,
    },
    professionalItems: {
      type: Array,
      default: () => [],
    },
    showProfessionalFields: {
      type: Boolean,
      default: false,
    },
    showBrokerCodeTooltip: {
      type: Boolean,
      default: false,
    },
    showPartnershipHint: {
      type: Boolean,
      default: false,
    },
    disableResponsibleProfessional: {
      type: Boolean,
      default: false,
    },
    responsibleReturnObject: {
      type: Boolean,
      default: false,
    },
    clientData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      benchmarkMeta: { tipo: 'none', indexador: 'CDI', valor: null },
    }
  },
  computed: {
    safeData() {
      return this.clientData || {}
    },
    shouldShowProfessionalSection() {
      return this.mode === 'create' || this.showProfessionalFields
    },
    shouldShowEditOnlyFlags() {
      return this.shouldShowProfessionalSection && this.mode !== 'create'
    },
    benchmarkPersonalizadoPreview() {
      const m = this.benchmarkMeta
      if (!m || m.tipo === 'none') return null
      if (!m.indexador || !m.valor || m.valor <= 0) {
        return { error: true, message: 'Selecione o indexador e informe um valor válido' }
      }
      const valStr = String(m.valor).replace('.', ',')
      if (m.tipo === 'pos') return { error: false, message: `Pré-visualização: ${valStr}% do ${m.indexador}` }
      return { error: false, message: `Pré-visualização: ${m.indexador} + ${valStr}%` }
    },
    cpfValidationRules() {
      return [
        (value) => this.isEmptyValue({ value }) || rules.cpf(String(value)),
      ]
    },
    cnpjValidationRules() {
      return [
        (value) => this.isEmptyValue({ value }) || rules.cnpj(String(value)),
      ]
    },
    phoneValidationRules() {
      return [
        (value) => this.isEmptyValue({ value }) || rules.phone(String(value)),
      ]
    },
    dateValidationRules() {
      return [rules.dateNotRequired]
    },
    nameValidationRules() {
      return [rules.required]
    },
    emailValidationRules() {
      return [rules.required, rules.email]
    },
    potentialLabel() {
      return this.mode === 'edit' ? 'Patrimônio potencial' : 'Patrimônio'
    },
    visibleFields() {
      return this.fieldDefinitions.filter((field) => field && field.isVisible)
    },
    sections() {
      const groups = [
        {
          id: 'basic',
          title: 'Informações básicas',
          ids: [
            'name',
            'email',
            'birth-date',
            'cpf-basic',
            'cpf-professional',
            'phone',
            'profile',
            'profile-suggest',
            'profile-score',
            'potential',
            'broker-code',
            'fee-start',
            'qualification-group',
            'consent-date',
            'risk-status',
          ],
        },
        {
          id: 'managerial',
          title: 'Informações Gerenciais',
          ids: ['responsible-professional', 'access-professionals', 'tags'],
        },
        {
          id: 'preferences',
          title: 'Preferências do Cliente',
          ids: ['benchmark-personalizado'],
        },
        {
          id: 'advanced',
          title: 'Configurações avançadas',
          ids: ['active-flag', 'access-toggles'],
        },
      ]
      const fieldById = {}
      this.visibleFields.forEach((field) => {
        fieldById[field.id] = field
      })
      return groups
        .map((group) => ({
          id: group.id,
          key: group.id,
          title: group.title,
          fields: group.ids.map((id) => fieldById[id]).filter(Boolean),
        }))
        .filter((group) => group.fields.length > 0)
    },
    defaultOpenSectionKeys() {
      return ['basic', 'managerial']
    },
    fieldDefinitions() {
      return [
        this.buildNameField(),
        this.buildEmailField(),
        this.buildBirthDateField(),
        this.buildCpfBasicField(),
        this.buildCpfProfessionalField(),
        this.buildPhoneField(),
        this.buildProfileField(),
        this.buildProfileSuggestField(),
        this.buildProfileScoreField(),
        this.buildPotentialField(),
        this.buildBrokerCodeField(),
        this.buildFeeStartField(),
        this.buildQualificationGroupField(),
        this.buildConsentDateField(),
        this.buildRiskStatusField(),
        this.buildResponsibleProfessionalField(),
        this.buildAccessProfessionalsField(),
        this.buildTagsField(),
        this.buildBenchmarkPersonalizadoField(),
        this.buildActiveFlagField(),
        this.buildAccessTogglesField(),
      ]
    },
  },
  watch: {
    'safeData.benchmark_personalizado': {
      immediate: true,
      handler(formula) {
        this.benchmarkMeta = this.parseFormulaToMeta(formula)
      },
    },
  },
  methods: {
    emitFieldUpdate({ fieldName, value }) {
      const baseData = this.clientData || {}
      this.$emit('update:clientData', { ...baseData, [fieldName]: value })
    },
    isEmptyValue({ value }) {
      if (value === null || value === undefined) {
        return true
      }
      return String(value).trim().length === 0
    },
    preventMinusKey({ event }) {
      if (event && event.key === '-') {
        event.preventDefault()
      }
    },
    handleFieldKeydown({ field, event }) {
      if (field && field.onKeydown) {
        field.onKeydown(event)
      }
    },
    buildFieldWrapperClass({ fieldId }) {
      return ['client-fields__field', `client-fields__field--${fieldId}`]
    },
    buildNameField() {
      return {
        id: 'name',
        cols: 6,
        label: 'Nome*',
        kind: 'input',
        isVisible: true,
        wrapperClass: this.buildFieldWrapperClass({ fieldId: 'name' }),
        bind: {
          value: this.safeData.nome_cliente,
          class: 'new-ux',
          outlined: true,
          rules: this.nameValidationRules,
        },
        onInput: (value) =>
          this.emitFieldUpdate({ fieldName: 'nome_cliente', value }),
      }
    },
    buildEmailField() {
      return {
        id: 'email',
        cols: 6,
        label: 'Email*',
        kind: 'input',
        isVisible: true,
        wrapperClass: this.buildFieldWrapperClass({ fieldId: 'email' }),
        bind: {
          value: this.safeData.email,
          type: 'email',
          class: 'new-ux',
          outlined: true,
          rules: this.emailValidationRules,
        },
        onInput: (value) => this.emitFieldUpdate({ fieldName: 'email', value }),
      }
    },
    buildBirthDateField() {
      return {
        id: 'birth-date',
        cols: 4,
        label: 'Data de nascimento',
        kind: 'input',
        isVisible: true,
        wrapperClass: this.buildFieldWrapperClass({ fieldId: 'birth-date' }),
        bind: {
          value: this.safeData.data_nascimento,
          type: 'date',
          placeholder: 'DD/MM/AAAA',
          class: 'new-ux align-icon-search',
          outlined: true,
          rules: this.dateValidationRules,
        },
        onInput: (value) =>
          this.emitFieldUpdate({ fieldName: 'data_nascimento', value }),
      }
    },
    buildPhoneField() {
      return {
        id: 'phone',
        cols: 4,
        label: 'Telefone',
        kind: 'input',
        isVisible: true,
        wrapperClass: this.buildFieldWrapperClass({ fieldId: 'phone' }),
        bind: {
          id: 'telefone',
          value: this.safeData.telefone,
          type: 'telefone',
          class: 'new-ux',
          outlined: true,
          rules: this.phoneValidationRules,
        },
        onInput: (value) =>
          this.emitFieldUpdate({ fieldName: 'telefone', value }),
      }
    },
    buildProfileField() {
      return {
        id: 'profile',
        cols: 4,
        label: 'Perfil',
        kind: 'select',
        isVisible: this.shouldShowProfessionalSection,
        wrapperClass: this.buildFieldWrapperClass({ fieldId: 'profile' }),
        bind: {
          value: this.safeData.perfil,
          items: this.profileItems,
          placeholder: 'Selecionar',
          loading: this.profileLoading,
          class: 'new-ux',
          outlined: true,
        },
        onInput: (value) =>
          this.emitFieldUpdate({ fieldName: 'perfil', value }),
      }
    },
    buildProfileSuggestField() {
      return {
        id: 'profile-suggest',
        cols: 4,
        label: 'Perfil sugerido',
        kind: 'input',
        isVisible: this.shouldShowProfessionalSection,
        wrapperClass: this.buildFieldWrapperClass({
          fieldId: 'profile-suggest',
        }),
        bind: {
          value: this.safeData.perfil_sugerido,
          type: 'text',
          placeholder: '',
          loading: this.profileLoading,
          class: 'new-ux',
          outlined: true,
          readonly: true,
        },
        onInput: (value) =>
          this.emitFieldUpdate({ fieldName: 'perfil_sugerido', value }),
      }
    },
    buildProfileScoreField() {
      return {
        id: 'profile-score',
        cols: 4,
        label: 'Pontuação do perfil',
        kind: 'input',
        isVisible: this.shouldShowProfessionalSection,
        wrapperClass: this.buildFieldWrapperClass({
          fieldId: 'profile-score',
        }),
        bind: {
          value: this.safeData.pontuacao_perfil,
          type: 'text',
          placeholder: '',
          loading: this.profileLoading,
          class: 'new-ux',
          outlined: true,
          readonly: true,
        },
        onInput: (value) =>
          this.emitFieldUpdate({ fieldName: 'pontuacao_perfil', value }),
      }
    },
    buildCpfBasicField() {
      const isPj = !!this.safeData.cliente_pj
      return {
        id: 'cpf-basic',
        cols: 4,
        label: isPj ? 'CNPJ' : 'CPF',
        kind: 'input',
        isVisible: !this.shouldShowProfessionalSection,
        wrapperClass: this.buildFieldWrapperClass({ fieldId: 'cpf-basic' }),
        bind: {
          value: this.safeData.cpf,
          type: isPj ? 'cnpj' : 'cpf',
          class: 'new-ux',
          outlined: true,
          rules: isPj ? this.cnpjValidationRules : this.cpfValidationRules,
        },
        onInput: (value) => this.emitFieldUpdate({ fieldName: 'cpf', value }),
      }
    },
    buildPotentialField() {
      return {
        id: 'potential',
        cols: 4,
        label: this.potentialLabel,
        kind: 'input',
        isVisible: this.shouldShowProfessionalSection,
        wrapperClass: this.buildFieldWrapperClass({ fieldId: 'potential' }),
        bind: {
          value: this.safeData.pl_potencial,
          type: 'money',
          class: 'new-ux',
          outlined: true,
          allowNegative: false,
          placeholder: '0,00',
        },
        onInput: (value) =>
          this.emitFieldUpdate({ fieldName: 'pl_potencial', value }),
        onKeydown: (event) => this.preventMinusKey({ event }),
      }
    },
    buildCpfProfessionalField() {
      const isPj = !!this.safeData.cliente_pj
      return {
        id: 'cpf-professional',
        cols: 4,
        label: isPj ? 'CNPJ' : 'CPF',
        kind: 'input',
        isVisible: this.shouldShowProfessionalSection,
        wrapperClass: this.buildFieldWrapperClass({
          fieldId: 'cpf-professional',
        }),
        bind: {
          value: this.safeData.cpf,
          type: isPj ? 'cnpj' : 'cpf',
          class: 'new-ux',
          outlined: true,
          rules: isPj ? this.cnpjValidationRules : this.cpfValidationRules,
        },
        onInput: (value) => this.emitFieldUpdate({ fieldName: 'cpf', value }),
      }
    },
    buildBrokerCodeField() {
      return {
        id: 'broker-code',
        cols: 4,
        label: 'Código do cliente',
        kind: 'brokerCode',
        isVisible: this.shouldShowProfessionalSection,
        wrapperClass: this.buildFieldWrapperClass({ fieldId: 'broker-code' }),
        bind: {
          value: this.safeData.codigo_corretora,
          class: 'new-ux',
          outlined: true,
          maxlength: 23,
        },
        onInput: (value) =>
          this.emitFieldUpdate({ fieldName: 'codigo_corretora', value }),
      }
    },
    buildFeeStartField() {
      return {
        id: 'fee-start',
        cols: 6,
        md: 4,
        label: 'Início da cobrança de fee',
        kind: 'input',
        isVisible: this.shouldShowProfessionalSection,
        wrapperClass: this.buildFieldWrapperClass({ fieldId: 'fee-start' }),
        bind: {
          value: this.safeData.data_inicio_fee,
          type: 'date',
          placeholder: 'DD/MM/AAAA',
          class: 'new-ux align-icon-search',
          outlined: true,
          rules: this.dateValidationRules,
        },
        onInput: (value) =>
          this.emitFieldUpdate({ fieldName: 'data_inicio_fee', value }),
      }
    },
    accessFlagValue({ rawValue }) {
      // Semantica permissiva (spec-006): NULL e TRUE liberam acesso;
      // somente `false` explicito bloqueia o login do cliente naquele canal.
      // Equivalente a `rawValue === false ? false : true` (default ligado
      // quando undefined/null/true).
      return rawValue !== false
    },
    accessToggleValue({ rawValue }) {
      return this.accessFlagValue({ rawValue }) ? 'liberado' : 'bloqueado'
    },
    buildActiveFlagField() {
      return {
        id: 'active-flag',
        cols: 12,
        kind: 'statusToggle',
        isVisible: this.shouldShowEditOnlyFlags,
        wrapperClass: this.buildFieldWrapperClass({ fieldId: 'active-flag' }),
        label: 'Status do cliente',
        value: this.safeData.ativo === false ? 'inativo' : 'ativo',
        options: CLIENT_STATUS_TOGGLE_OPTIONS,
        onChange: (value) =>
          this.emitFieldUpdate({ fieldName: 'ativo', value: value === 'ativo' }),
      }
    },
    buildAccessTogglesField() {
      return {
        id: 'access-toggles',
        cols: 12,
        kind: 'accessToggles',
        isVisible: this.shouldShowEditOnlyFlags,
        wrapperClass: this.buildFieldWrapperClass({ fieldId: 'access-toggles' }),
        group: this.buildAccessTogglesGroupField(),
      }
    },
    buildAccessTogglesGroupField() {
      return {
        id: 'access-toggles-group',
        hint: 'Bloqueado impede o login do cliente naquele canal.',
        items: [
          {
            id: 'access-web',
            label: 'Acesso Web',
            value: this.accessToggleValue({
              rawValue: this.safeData.possui_acesso_web,
            }),
            options: CLIENT_ACCESS_TOGGLE_OPTIONS,
            onChange: (value) =>
              this.emitFieldUpdate({
                fieldName: 'possui_acesso_web',
                value: value === 'liberado',
              }),
          },
          {
            id: 'access-app',
            label: 'Acesso App',
            value: this.accessToggleValue({
              rawValue: this.safeData.possui_acesso_app,
            }),
            options: CLIENT_ACCESS_TOGGLE_OPTIONS,
            onChange: (value) =>
              this.emitFieldUpdate({
                fieldName: 'possui_acesso_app',
                value: value === 'liberado',
              }),
          },
        ],
      }
    },
    buildQualificationGroupField() {
      return {
        id: 'qualification-group',
        cols: 6,
        md: 8,
        kind: 'checkboxGroup',
        isVisible: this.shouldShowProfessionalSection,
        wrapperClass: this.buildFieldWrapperClass({
          fieldId: 'qualification-group',
        }),
        items: [
          {
            id: 'qualified',
            label: 'Qualificado',
            value: !!this.safeData.qualificado,
            onChange: (value) =>
              this.emitFieldUpdate({ fieldName: 'qualificado', value }),
          },
          {
            id: 'pj',
            label: 'Pessoa Jurídica',
            value: !!this.safeData.cliente_pj,
            onChange: (value) => {
              const baseData = this.clientData || {}
              this.$emit('update:clientData', {
                ...baseData,
                cliente_pj: value,
                cpf: '',
              })
            },
          },
          {
            id: 'lead',
            label: 'Lead',
            value: !!this.safeData.cliente_prospect,
            onChange: (value) =>
              this.emitFieldUpdate({ fieldName: 'cliente_prospect', value }),
          },
          {
            id: 'investidor-profissional',
            label: 'Investidor Profissional',
            value: !!this.safeData.investidor_profissional,
            onChange: (value) =>
              this.emitFieldUpdate({ fieldName: 'investidor_profissional', value }),
          },
          {
            id: 'adesao-consentimento',
            label: 'Adesão ao Consentimento de Risco',
            value: !!this.safeData.adesao_consentimento_risco,
            onChange: (value) => {
              this.emitFieldUpdate({ fieldName: 'adesao_consentimento_risco', value })

              if (!value) {
                this.emitFieldUpdate({
                  fieldName: 'data_adesao_consentimento_risco',
                  value: null,
                })
              }
            },
          },
        ],
      }
    },
    buildConsentDateField() {
      return {
        id: 'consent-date',
        cols: 6,
        md: 4,
        label: 'Data da Adesão ao Consentimento',
        kind: 'input',
        isVisible: this.shouldShowProfessionalSection && !!this.safeData.adesao_consentimento_risco,
        wrapperClass: this.buildFieldWrapperClass({ fieldId: 'consent-date' }),
        bind: {
          value: this.safeData.data_adesao_consentimento_risco,
          type: 'date',
          placeholder: 'DD/MM/AAAA',
          class: 'new-ux align-icon-search',
          outlined: true,
          rules: this.dateValidationRules,
        },
        onInput: (value) =>
          this.emitFieldUpdate({ fieldName: 'data_adesao_consentimento_risco', value }),
      }
    },
    buildRiskStatusField() {
      return {
        id: 'risk-status',
        cols: 6,
        md: 4,
        label: 'Status de Desenquadramento',
        kind: 'input',
        isVisible: this.shouldShowProfessionalSection && !!this.safeData.status_desenquadramento,
        wrapperClass: this.buildFieldWrapperClass({ fieldId: 'risk-status' }),
        bind: {
          value: this.safeData.status_desenquadramento,
          type: 'text',
          class: 'new-ux',
          outlined: true,
          readonly: true,
        },
        onInput: () => {},
      }
    },
    buildResponsibleProfessionalField() {
      return {
        id: 'responsible-professional',
        cols: 4,
        label: 'Profissional responsável',
        kind: 'select',
        isVisible: this.shouldShowProfessionalSection,
        wrapperClass: this.buildFieldWrapperClass({
          fieldId: 'responsible-professional',
        }),
        bind: {
          value: this.safeData.id_profissional_responsavel,
          items: this.professionalItems,
          placeholder: 'Selecionar',
          itemText: 'nome',
          itemValue: 'id_profissional',
          returnObject: this.responsibleReturnObject,
          disabled: this.disableResponsibleProfessional,
          class: 'new-ux',
          outlined: true,
        },
        onInput: (value) =>
          this.emitFieldUpdate({
            fieldName: 'id_profissional_responsavel',
            value,
          }),
      }
    },
    buildAccessProfessionalsField() {
      return {
        id: 'access-professionals',
        cols: 8,
        label: 'Profissionais com acesso',
        kind: 'autocompleteMany',
        isVisible: this.shouldShowProfessionalSection,
        wrapperClass: this.buildFieldWrapperClass({
          fieldId: 'access-professionals',
        }),
        bind: {
          id: 'id_profissional',
          selected: this.safeData.assessores_comerciais,
          items: this.professionalItems,
          text: 'nome',
          multiple: true,
          selectedItemsToShow: 5,
          class: 'cadastro-cliente__label--select-multiple',
        },
        onUpdateSelected: (value) =>
          this.emitFieldUpdate({ fieldName: 'assessores_comerciais', value }),
      }
    },
    buildTagsField() {
      return {
        id: 'tags',
        cols: 4,
        kind: 'tags',
        isVisible: this.shouldShowProfessionalSection,
        wrapperClass: this.buildFieldWrapperClass({ fieldId: 'tags' }),
        bind: {
          value: this.safeData.tags,
          externalLabel: true,
          outlined: true,
          small: true,
        },
        onInput: (value) => this.emitFieldUpdate({ fieldName: 'tags', value }),
      }
    },
    buildBenchmarkPersonalizadoField() {
      return {
        id: 'benchmark-personalizado',
        cols: 12,
        label: 'Benchmark personalizado',
        kind: 'benchmarkPersonalizadoStructured',
        isVisible: this.shouldShowProfessionalSection,
        meta: this.benchmarkMeta,
        indexadores: BENCHMARK_INDEXADORES,
        preview: this.benchmarkPersonalizadoPreview,
        onTipo: (tipo) => {
          const anteriorTipo = this.benchmarkMeta.tipo
          this.benchmarkMeta.tipo = tipo
          // Limpa valor ao trocar tipo para evitar spreads absurdos (ex: "120% IPC-A")
          if (anteriorTipo !== tipo && tipo !== 'none') this.benchmarkMeta.valor = null
          this.emitBenchmarkMeta()
        },
        onIndexador: (indexador) => {
          this.benchmarkMeta.indexador = indexador
          this.emitBenchmarkMeta()
        },
        onValor: (valor) => {
          this.benchmarkMeta.valor = valor === '' || valor === null ? null : Number(valor)
          this.emitBenchmarkMeta()
        },
      }
    },
    parseFormulaToMeta(formula) {
      return parseClienteBenchmarkFormula(formula)
    },
    normalizeIndexador(raw) {
      return normalizeClienteBenchmarkIndexador(raw)
    },
    emitBenchmarkMeta() {
      const m = this.benchmarkMeta
      // Clear explícito: usuário selecionou "Sem benchmark"
      if (m.tipo === 'none') {
        this.emitFieldUpdate({ fieldName: 'benchmark_personalizado', value: null })
        return
      }
      // Fórmula incompleta: não emitir para evitar feedback loop com o watcher
      // (watcher reseta benchmarkMeta ao receber null, fazendo o radio voltar p/ 'none')
      const formula = buildClienteBenchmarkFormula(m)
      if (!formula) return
      this.emitFieldUpdate({ fieldName: 'benchmark_personalizado', value: formula })
    },
  },
}
</script>

<template>
  <div class="client-fields">
    <ExpandableGroupTable
      :groups="sections"
      :expanded-keys="defaultOpenSectionKeys"
    >
      <template v-slot:content="{ group }">
        <v-row>
            <v-col
              v-for="field in group.fields"
              :key="field.id"
              :cols="field.cols"
              :md="field.md"
              :class="field.wrapperClass"
            >
              <template v-if="field.kind === 'checkboxGroup'">
                <div class="d-flex az-gap align-center flex-wrap h-100">
                  <v-checkbox
                    v-for="item in field.items"
                    :key="item.id"
                    :input-value="item.value"
                    :label="item.label"
                    @change="item.onChange($event)"
                  />
                </div>
              </template>

              <template v-else-if="field.kind === 'statusToggle'">
                <div class="az-text-label"> {{ field.label }} </div>
                <BaseToggleButton
                  :value="field.value"
                  :options="field.options"
                  small
                  @input="field.onChange($event)"
                />
              </template>

              <template v-else-if="field.kind === 'accessToggles'">
                <div class="d-flex az-gap flex-wrap mt-4">
                  <div
                    v-for="item in field.group.items"
                    :key="item.id"
                    class="d-flex flex-column"
                  >
                    <div class="az-text-label mb-1"> {{ item.label }} </div>
                    <BaseToggleButton
                      :value="item.value"
                      :options="item.options"
                      small
                      @input="item.onChange($event)"
                    />
                  </div>
                </div>
                <div class="az-text-small mt-1"> {{ field.group.hint }} </div>
              </template>

              <template v-else-if="field.kind === 'tags'">
                <TagsField v-bind="field.bind" @input="field.onInput($event)" />
              </template>

              <template v-else-if="field.kind === 'autocompleteMany'">
                <div class="az-text-label"> {{ field.label }} </div>

                <AutocompleteMany
                  v-bind="field.bind"
                  @update:selected="field.onUpdateSelected($event)"
                />
              </template>

              <template v-else-if="field.kind === 'brokerCode'">
                <div class="az-text-label"> {{ field.label }} </div>

                <v-text-field v-bind="field.bind" @input="field.onInput($event)">
                  <template v-if="showBrokerCodeTooltip" v-slot:append>
                    <v-tooltip max-width="300" top>
                      <template v-slot:activator="{ on }">
                        <v-icon size="20" v-on="on"> mdi-information-outline </v-icon>
                      </template>
                      <span>
                        Utilize esse código para controle interno
                        <template v-if="showPartnershipHint">
                          ou para fazer o link com clientes que foram importados no
                          módulo de Partnership da plataforma
                        </template>
                      </span>
                    </v-tooltip>
                  </template>
                </v-text-field>
              </template>

              <template v-else-if="field.kind === 'select'">
                <div class="az-text-label"> {{ field.label }} </div>

                <v-select v-bind="field.bind" @input="field.onInput($event)" />
              </template>

              <template
                v-else-if="field.kind === 'benchmarkPersonalizadoStructured'"
              >
                <div class="az-text-label">{{ field.label }}</div>
                <v-radio-group
                  :value="field.meta.tipo"
                  row
                  dense
                  hide-details
                  class="mt-1 mb-2"
                  @change="field.onTipo($event)"
                >
                  <v-radio label="Nenhum (usa CDI)" value="none" />
                  <v-radio label="Pós-fixado: % do indexador" value="pos" />
                  <v-radio label="Pré-fixado: indexador + spread" value="pre" />
                </v-radio-group>
                <v-row v-if="field.meta.tipo !== 'none'" no-gutters class="mt-1">
                  <v-col cols="6" class="pr-2">
                    <v-select
                      :value="field.meta.indexador"
                      :items="field.indexadores"
                      label="Indexador"
                      outlined
                      dense
                      hide-details
                      @change="field.onIndexador($event)"
                    />
                  </v-col>
                  <v-col cols="6">
                    <input-by-type
                      :value="field.meta.valor"
                      type="decimal"
                      label="Valor (%)"
                      outlined
                      dense
                      hide-details
                      @input="field.onValor($event)"
                    />
                  </v-col>
                </v-row>
                <div
                  v-if="field.preview"
                  class="client-fields__benchmark-preview mt-2 mb-1"
                  :class="field.preview.error ? 'error--text' : 'success--text'"
                  style="font-size: 12px;"
                >
                  {{ field.preview.message }}
                </div>
                <div class="client-fields__benchmark-hint caption grey--text">
                  Após salvar, re-cotize o cliente para o benchmark aparecer nos relatórios.
                </div>
              </template>

              <template v-else>
                <div class="az-text-label"> {{ field.label }} </div>

                <input-by-type
                  v-bind="field.bind"
                  @input="field.onInput($event)"
                  @keydown="handleFieldKeydown({ field, event: $event })"
                />
              </template>
            </v-col>
          </v-row>
      </template>
    </ExpandableGroupTable>
  </div>
</template>

<style lang="sass">
.client-fields
  .v-input--is-readonly
    fieldset
      border-style: dashed
      background: var(--gray-3)
</style>
